import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Pagination from "@mui/material/Pagination";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Close";

import { get } from "lib/api";
import MobilePage from "mobile/components/MobilePage";
import { IosFilterBar, IosList, IosListItem, iosColors } from "mobile/components/IosParity";

const normalize = (d) => (d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id });

const STATUS_FILTERS = [
  { key: "all", label: "All", icon: "filter_list" },
  { key: "active", label: "Active", icon: "play_circle", accent: "orange" },
  { key: "draft", label: "Draft", icon: "edit_note" },
  { key: "on_hold", label: "On hold", icon: "pause_circle", accent: "red" },
  { key: "complete", label: "Done", icon: "check_circle", accent: "green" },
  { key: "shipped", label: "Shipped", icon: "local_shipping", accent: "green" },
];

export default function WorkOrdersListMobile() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  useEffect(() => {
    setPage(1);
  }, [status, search]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["m-work-orders", status, search, page, pageSize],
    queryFn: () =>
      get("/api/work-orders", {
        populate: "*",
        sort: "updatedAt:desc",
        pagination: { page, pageSize },
        filters: {
          ...(status !== "all" && { status: { $eq: status } }),
          ...(search && {
            $or: [{ workOrderNumber: { $containsi: search } }, { customerRef: { $containsi: search } }],
          }),
        },
      }),
    staleTime: 5_000,
  });

  const rows = useMemo(() => (data?.data || []).map(normalize), [data]);
  const pageCount = data?.meta?.pagination?.pageCount || 1;
  const total = data?.meta?.pagination?.total;

  return (
    <MobilePage
      title="Work Orders"
      subtitle={
        typeof total === "number"
          ? `${total} total • page ${page}/${pageCount}`
          : rows.length
          ? `${rows.length} shown`
          : " "
      }
      right={
        isFetching ? (
          <Chip size="small" label="Syncing…" variant="outlined" />
        ) : null
      }
      onBack={() => navigate("/m/dashboard")}
    >
      <Box sx={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: iosColors.canvas, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search WO # or customer ref…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                  sx={{ color: "#8F3700", bgcolor: "rgba(255,106,0,0.07)" }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <IosFilterBar label="Status" value={status} options={STATUS_FILTERS} onChange={setStatus} />
      </Box>

      {isLoading ? <LinearProgress sx={{ mb: 1 }} /> : null}
      {error ? (
        <Typography variant="body2" color="error" sx={{ px: 0.5, py: 1 }}>
          Failed to load work orders.
        </Typography>
      ) : null}

      <IosList>
        {rows.map((wo) => {
          const documentId = wo.documentId;
          const due = wo.dueDate ? new Date(wo.dueDate).toLocaleDateString() : null;
          return (
            <IosListItem
              key={documentId || wo.id}
              icon="build"
              title={wo.workOrderNumber || "Work Order"}
              meta={`${wo.customerRef || "No customer reference"}${due ? ` • Due ${due}` : ""}`}
              trailing={String(wo.status || "unknown").replace("_", " ")}
              accent={wo.status === "complete" || wo.status === "shipped" ? "green" : wo.status === "on_hold" ? "red" : "orange"}
              onClick={() => {
                if (documentId) navigate(`/m/work-orders/${documentId}`);
              }}
            />
          );
        })}

        {!isLoading && !rows.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 0.5, py: 2 }}>
            No work orders found.
          </Typography>
        ) : null}
      </IosList>

      {pageCount > 1 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <Pagination
            size="small"
            count={pageCount}
            page={page}
            onChange={(e, p) => setPage(p)}
            sx={{
              "& .MuiPaginationItem-root": {
                fontWeight: 900,
                borderRadius: 1,
              },
              "& .Mui-selected": {
                bgcolor: "#B54708 !important",
                color: "#fff",
              },
            }}
          />
        </Box>
      ) : null}
    </MobilePage>
  );
}
